import { Component, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouteConfigLoadEnd, Router, RouterOutlet } from '@angular/router';
import { MotifProgressLoaderModule, MotifThemeModule } from '@ey-xd/ng-motif';
import { THEME } from '@models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MainHelperService } from '@services';
import { EytcAuthService } from '@services/authentication/eytc-auth.service';
import { CompanyContextService } from '@services/company-context/company-context.service';
import { LoadingService } from '@services/loader/loading.service';
import { filter, tap } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, MotifThemeModule, TranslateModule, MotifProgressLoaderModule],
  templateUrl: './app.component.html',
  standalone: true,
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  #destroyRef: DestroyRef = inject(DestroyRef);
  #companyContextService = inject(CompanyContextService);
  #mainHelperService: MainHelperService = inject(MainHelperService);
  #translate: TranslateService = inject(TranslateService);
  #eytcAuth: EytcAuthService = inject(EytcAuthService);
  #loadingService = inject(LoadingService);
  #router = inject(Router);

  public themeVariant: WritableSignal<string> = signal<string>(THEME.LIGHT);

  public allowLoader = signal<boolean>(true);
  public showLoader = this.#loadingService.loading;

  constructor() {
    this.#router.events
      .pipe(
        filter(e => e instanceof RouteConfigLoadEnd),
        tap(e => {
          const routeData = e.route.data;
          const allowLoader = routeData ? routeData['allowLoader'] : null;

          if (allowLoader !== null && allowLoader !== undefined) {
            console.log('Setting allowLoader to', allowLoader);
            this.allowLoader.set(allowLoader);
          }
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.#initializeTranslation();
    this.#initializeTheme();
    this.#subscribeToThemeToggle();
    this.#subscribeToCompanySelectedEvent();
    this.#updateFaviconForInitialBrowserTheme();

    this.#eytcAuth.init();

    this.#eytcAuth.getUserLoginState$();
  }

  #initializeTranslation(): void {
    this.#translate.addLangs(['en']);
    this.#translate.setDefaultLang('en');
    this.#translate.use('en');
  }

  #initializeTheme(): void {
    this.#mainHelperService.initializeTheme();
    this.themeVariant.set(this.#mainHelperService.theme());
  }

  #subscribeToThemeToggle(): void {
    this.#mainHelperService
      .subscribeToThemeToggle()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((theme: boolean): void => {
        this.themeVariant.set(theme ? THEME.DARK : THEME.LIGHT);
      });
  }

  #updateFaviconForInitialBrowserTheme(): void {
    const iconPath = this.#mainHelperService.lightQuery.matches
      ? 'assets/favicon-light.ico'
      : 'assets/favicon-dark.ico';
    this.#mainHelperService.setFavicon(iconPath);
  }

  #subscribeToCompanySelectedEvent(): void {
    this.#mainHelperService.subscribeToCompanyChange().subscribe(selectedCompany => {
      this.#companyContextService.currentCompany.set(selectedCompany);
    });
  }
}
